import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

import { Button, Col, Input, InputNumber, Popconfirm, Row, Space, Statistic, Table } from 'antd'
import { CloseOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons'

import apiConfig from '../../../../config/apiConfig'

const renderCartSummary = (summary) => {
  if (!summary) return null

  const { itemsTotalWeight, itemsTotalPrice, subItemsTotalPrice } = summary
  const totalPrice = parseFloat(itemsTotalPrice.replace(',', '')) + parseFloat(subItemsTotalPrice.replace(',', ''))

  return (
    <Row gutter={[16, 12]}>
      <Col xs={12} md={6}>
        <Statistic
          title="Total Weight"
          value={itemsTotalWeight}
          valueStyle={{ color: '#c8c8c8' }}
          precision={2} />
      </Col>
      <Col xs={12} md={6}>
        <Statistic
          title="Item Total Price"
          value={itemsTotalPrice}
          precision={2} />
      </Col>
      <Col xs={12} md={6}>
        <Statistic
          title="Sub Items Total Price"
          value={subItemsTotalPrice}
          precision={2} />
      </Col>
      <Col xs={12} md={6}>
        <Statistic
          title="Total Price"
          value={totalPrice}
          valueStyle={{ color: '#cf1322' }}
          precision={2} />
      </Col>
    </Row>
  )
}

const CartItemsComponent = ({
  dataSource,
  loading,
  onAddItemToCart,
  onRemoveCartItems,
  onUpdateQuantity,
  summary,
  editorState,
  externalIDState,
  quantityState
}) => {
  const columns = [{
    key: 'id',
    title: 'Cart Item ID',
    dataIndex: 'id'
  }, {
    key: 'displayName',
    title: 'Display Name (External ID)',
    render: (_, record) => {
      const { displayName, item, cartItemCustomDimensions } = record
      const { id: externalId, url } = item || {}
      return (
        <div>
          <a href={`${apiConfig.apiBase}${url}`} target="_blank" rel="noopener noreferrer">
            {`${displayName} (${externalId})`}
          </a>
          {cartItemCustomDimensions && cartItemCustomDimensions.length > 0 && (
            <div>
              {cartItemCustomDimensions.map(dim => (
                <span key={dim.id}>
                  {dim.value} x {dim.customDimensionDimension.unit}
                </span>
              ))}
            </div>
          )}
        </div>
      )
    }
  }, {
    key: 'itemPrice',
    title: 'Single Price',
    dataIndex: 'itemPrice'
  }, {
    key: 'quantity',
    title: 'Quantity',
    render: (_, record) => {
      if (editorState.name === 'quantity' && editorState.current === record.id) {
        return (
          <Space>
            <InputNumber
              defaultValue={record.quantity}
              onPressEnter={() => onUpdateQuantity(record)}
              onChange={editorState.change}
              value={editorState.value}
              min={1}
              style={{ width: 80 }} />
            <Button
              icon={<SaveOutlined />}
              onClick={() => onUpdateQuantity(record)}
              size="small"
              type="link" />
              <Button
                icon={<CloseOutlined />}
                onClick={editorState.close}
                size="small"
                type="link" />
          </Space>
        )
      } else {
        return (
          <Space>
            <span>{record.quantity}</span>
            <Button
              icon={<EditOutlined />}
              onClick={() => editorState.open({ name: 'quantity', record })}
              size="small"
              type="link" />
          </Space>
        )
      }
    }
  }, {
    key: 'unit',
    title: 'Unit',
    render: (_, record) => record.item ? record.item.unit: '-'
  }, {
    key: 'displayTotalWeight',
    title: 'Total Weight',
    render: (_, record) => `${record.displayTotalWeight} kg`
  }, {
    key: 'itemsTotal',
    title: 'Item Total Price',
    dataIndex: 'itemsTotal'
  }, {
    key: 'servicePrice',
    title: 'Service Price',
    render: (_, record) => {
      const servicePrice = record.subCartServiceItemsTotalPrice.display
      return servicePrice > 0 ? `${servicePrice} (${record.subItemsCount})` : '-'
    }
  },
  {
    key: 'totalPrice',
    title: 'Total Price',
    render: (_, record) => {
      const servicePrice = record.subCartServiceItemsTotalPrice.display || '0'
      return parseFloat(record.itemsTotal.replace(',', '')) + parseFloat(servicePrice.replace(',', ''))
    }
  },
  {
    key: 'actions',
    render: (_, record) => (
      <Popconfirm title="Confirm delete?" onConfirm={() => onRemoveCartItems([record.id])}>
        <Button type="link" icon={<DeleteOutlined />} danger />
      </Popconfirm>
    )
  }]

  return (
    <Fragment>
      <Col xs={24} md={10}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <h2 style={{ margin: '0' }}>Cart Items</h2>
          <Space wrap>
            <Input
              placeholder="Item External ID"
              style={{ width: 200 }}
              {...externalIDState} />
            <InputNumber
              placeholder="Quantity"
              min={1}
              style={{ width: 100 }}
              {...quantityState} />
            <Button type="primary" onClick={onAddItemToCart}>Add to Cart</Button>
          </Space>
        </Space>
      </Col>

      <Col xs={24} md={14}>
        {renderCartSummary(summary)}
      </Col>

      <Col span={24}>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          rowKey="id"
          scroll={{ x: 1200 }}
          size="middle" />
      </Col>
    </Fragment>
  )
}

CartItemsComponent.propTypes = {
  dataSource: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onAddItemToCart: PropTypes.func.isRequired,
  onRemoveCartItems: PropTypes.func.isRequired,
  onUpdateQuantity: PropTypes.func.isRequired,
  editorState: PropTypes.shape({
    name: PropTypes.string,
    current: PropTypes.string,
    value: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    open: PropTypes.func.isRequired
  }),
  externalIDState: PropTypes.shape({
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired
  }),
  quantityState: PropTypes.shape({
    value: PropTypes.number,
    onChange: PropTypes.func.isRequired
  })
}

export default CartItemsComponent
